import { FieldError } from '@civilgrid/ui/components';
import { useId, useState } from 'react';
import DatePicker from 'react-datepicker';
import { type Control, Controller, type UseFormSetValue } from 'react-hook-form';
import 'react-datepicker/dist/react-datepicker.css';
import { isEmpty } from 'lodash';
import type { TransactionFields } from '~/pages/admin/transactions/common';

interface Props {
  control: Control<TransactionFields>;
  name: 'transactionDate';
  label: string;
  current: string;
  setValue: UseFormSetValue<TransactionFields>;
  minDate?: string;
}

export default function Calendar({ control, name, label, current, setValue, minDate }: Props) {
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date(current));
  const errorId = useId();

  const setDateAndUpdate = (date: Date) => {
    setSelectedDate(date);
    setValue(name, date.toISOString(), { shouldDirty: true });
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: `${label} is required` }}
      render={({ fieldState: { error } }) => {
        return (
          <div className="relative flex w-full flex-col">
            {minDate ? (
              <DatePicker
                selected={selectedDate}
                closeOnScroll={true}
                ariaInvalid={isEmpty(error) ? undefined : 'true'}
                ariaDescribedBy={isEmpty(error) ? undefined : errorId}
                onChange={(date: Date | null) => {
                  if (!date) return;
                  setDateAndUpdate(date);
                }}
                minDate={new Date(minDate)}
                placeholderText="Select a date"
                customInput={
                  <div className="flex w-full flex-col">
                    <span className="px-1 text-2xs uppercase">{label}</span>
                    <div className="flex w-full rounded-sm">
                      <div className="w-full border p-1 text-sm outline-none focus:border-blue-500 data-[has-error=true]:border-red-500">
                        {selectedDate?.toLocaleDateString()}
                      </div>
                      {!isEmpty(error) && <span aria-hidden="true" className="block w-2 shrink-0 bg-red-500" />}
                    </div>
                  </div>
                }
              />
            ) : (
              <DatePicker
                selected={selectedDate}
                closeOnScroll={true}
                ariaInvalid={isEmpty(error) ? undefined : 'true'}
                ariaDescribedBy={isEmpty(error) ? undefined : errorId}
                onChange={(date: Date | null) => {
                  if (!date) return;
                  setDateAndUpdate(date);
                }}
                placeholderText="Select a date"
                customInput={
                  <div className="flex w-full flex-col">
                    <span className="px-1 text-2xs uppercase">{label}</span>
                    <div className="flex w-full rounded-sm">
                      <div className="w-full border p-1 text-sm outline-none focus:border-blue-500 data-[has-error=true]:border-red-500">
                        {selectedDate?.toLocaleDateString()}
                      </div>
                      {!isEmpty(error) && <span aria-hidden="true" className="block w-2 shrink-0 bg-red-500" />}
                    </div>
                  </div>
                }
              />
            )}
            {!isEmpty(error) && (
              <FieldError id={errorId} className="text-xs" data-testid={`${name}-error-details`}>
                {error?.message}
              </FieldError>
            )}
          </div>
        );
      }}
    />
  );
}
